"use client";

import { useScrollDirection } from "@/hooks/scrollDirection";
import { Link, usePathname, useRouter } from "@/i18n/navigation";
import { SiteDetail } from "@/interfaces/site-model";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useParams } from "next/navigation";
import { useEffect, useState } from "react";
import { HiBars3BottomRight } from "react-icons/hi2";
import { FaChevronRight } from "react-icons/fa";
import Sidebar from "../Sidebar/Sidebar";

let links = [
  { path: "/", label: "home" },
  { path: "/about-us", label: "about us" },
  { path: "", label: "our services", children: [] },
  { path: "/destinations", label: "destinations" },
  { path: "/our-offers", label: "our offers" },
  { path: "/our-blogs", label: "our blogs" },
  { path: "/contact-us", label: "contact us" },
];

const Navbar = (props: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState("home");
  const pathname = usePathname();
  const t = useTranslations("Header");
  const scrollDirection = useScrollDirection();
  const router = useRouter();
  const { locale } = useParams();
  const siteData: SiteDetail = props.data;
  const services: any[] = props.services;

  const showSidebar = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (locale === "en") {
      document.dir = "ltr";
    } else {
      document.dir = "rtl";
    }
  }, [locale]);

  useEffect(() => {
    const setActiveNav = () => {
      if (pathname === "/" || pathname === "/en" || pathname === "/ar") {
        setCurrentPath("home");
      } else if (
        pathname === "/about-us" ||
        pathname === "/en/about-us" ||
        pathname === "/ar/about-us"
      ) {
        setCurrentPath("about us");
      } else if (pathname.includes("our-services")) {
        setCurrentPath("our services");
      } else if (pathname.includes("destinations")) {
        setCurrentPath("destinations");
      } else if (
        pathname === "/our-offers" ||
        pathname === "/en/our-offers" ||
        pathname === "/ar/our-offers"
      ) {
        setCurrentPath("our offers");
      } else if (
        pathname === "/our-blogs" ||
        pathname === "/en/our-blogs" ||
        pathname === "/ar/our-blogs"
      ) {
        setCurrentPath("our blogs");
      } else if (
        pathname === "/contact-us" ||
        pathname === "/en/contact-us" ||
        pathname === "/ar/contact-us"
      ) {
        setCurrentPath("contact us");
      }
    };
    setActiveNav();
  }, [pathname, currentPath]);

  const handleLanguageSwitch = () => {
    const newLocale = locale === "en" ? "ar" : "en";
    router.replace(`${pathname}`, { locale: newLocale });
  };

  if (services?.length) {
    let serviceParent = links.find((li) => li.label === "our services");
    serviceParent.children = services
      .map((service) => service)
      .sort((a: any, b: any) => a?.order - b?.order);
  }

  return (
    <>
      <header
        className={`fixed top-0 h-[136px] w-full flex justify-center items-center bg-black/30 backdrop-blur-md z-[1000] transition-all duration-300 ${
          scrollDirection === "down" ? "-top-[136px]" : "top-0"
        }`}
      >
        <div className="container flex justify-between items-center px-5">
          {/* Left */}
          <div className="flex items-center justify-start gap-6 text-[#5a558e]">
            <div className="relative w-[120px] h-[120px]">
              <Link
                href="/"
                className="absolute w-[120px] h-[120px] flex items-center justify-center"
              >
                <Image
                  src={siteData?.site_logo?.url}
                  alt={siteData?.site_logo?.alternativeText || "logo"}
                  className="w-full h-full object-contain"
                  priority
                  width={120}
                  height={120}
                  quality={90}
                  placeholder="blur"
                  blurDataURL={
                    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACklEQVR4nGMAAQAABQABDQottAAAAABJRU5ErkJggg=="
                  }
                />
              </Link>
            </div>
          </div>

          {/* Center */}
          <div className="hidden lg:flex justify-center items-center gap-10 capitalize">
            {links.map(({ label, path, children }, index) => (
              <div className="relative group" key={index}>
                {!children ? (
                  <Link
                    href={path}
                    className={`inline-flex text-white pb-2 border-b border-transparent transition-all duration-200 hover:border-white ${
                      currentPath === label ? "border-b border-white" : ""
                    }`}
                  >
                    {t(label)}
                  </Link>
                ) : (
                  <button
                    className={`inline-flex text-white pb-2 border-b border-transparent transition-all duration-200 hover:border-white capitalize ${
                      currentPath === label ? "border-b border-white" : ""
                    }`}
                  >
                    {t(label)}
                  </button>
                )}
                {children?.length && (
                  <div className="absolute rounded-md z-10 shadow-lg hidden bg-white py-4 px-2 top-[100%] text-gray-800 min-w-[250px] group-hover:flex flex-col">
                    {children.map((service, index: number) => {
                      const hasSubServices =
                        service?.sub_services?.length > 0;
                      return (
                        <div
                          key={index}
                          className="relative [&:hover>.sub-menu]:flex"
                        >
                          <Link
                            className="hover:bg-gray-200 p-2 rounded-sm text-left flex items-center justify-between gap-2"
                            href={`/our-services/${service?.slug}`}
                          >
                            {service?.title}
                            {hasSubServices && (
                              <FaChevronRight className={`w-3 h-3 text-gray-400 ${locale === "ar" ? "rotate-180" : ""}`} />
                            )}
                          </Link>
                          {hasSubServices && (
                            <div
                              className={`sub-menu absolute top-0 hidden flex-col ${locale === "ar" ? "right-full pr-1" : "left-full pl-1"}`}
                            >
                              <div className="rounded-md shadow-lg bg-white py-4 px-2 text-gray-800 min-w-[220px] flex flex-col">
                                {service.sub_services.map(
                                  (sub: any, subIndex: number) => (
                                    <Link
                                      key={subIndex}
                                      className="hover:bg-gray-200 p-2 rounded-sm text-left"
                                      href={`/our-services/${service?.slug}/${sub?.slug}`}
                                    >
                                      {sub?.title}
                                    </Link>
                                  ),
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* Right */}
          <div className="flex justify-end items-center gap-4">
            <Link
              target="_blank"
              href={`tel:${siteData?.contact_details?.phone_number}`}
              className="relative w-7 h-7 hidden sm:block"
            >
              <Image src="/assets/icons/phone.svg" alt="Phone Icon" fill />
            </Link>
            <Link
              target="_blank"
              href={`https://wa.me/${siteData?.contact_details?.whatsapp_number}`}
              className="relative w-7 h-7 hidden sm:block"
            >
              <Image
                src="/assets/icons/whatsapp.svg"
                alt="WhatsApp Icon"
                fill
              />
            </Link>
            <Link
              target="_blank"
              href={siteData?.social_links?.instagram_link || ""}
              className="relative w-7 h-7 hidden sm:block"
            >
              <Image
                src="/assets/icons/instagram.svg"
                alt="Instagram Icon"
                fill
              />
            </Link>
            <Link
              target="_blank"
              href={siteData?.social_links?.twitter_link || ""}
              className="relative w-7 h-7 hidden sm:block"
            >
              <Image src="/assets/icons/twitter.svg" alt="Twitter Icon" fill />
            </Link>

            <button
              className="bg-transparent border-none cursor-pointer w-12 h-12 relative"
              onClick={handleLanguageSwitch}
            >
              <Image
                src={
                  locale === "en"
                    ? "/assets/icons/language_switch.svg"
                    : "/assets/icons/en_switch.svg"
                }
                alt="switch"
                fill
              />
            </button>

            <button
              onClick={showSidebar}
              className="bg-transparent border-none cursor-pointer text-3xl text-white md:hidden"
              aria-label="Toggle Navigation"
            >
              <HiBars3BottomRight />
            </button>
          </div>
        </div>
      </header>
      <Sidebar
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        menu={links}
        data={siteData}
      />
    </>
  );
};

export default Navbar;
